import { GetServerSideProps, NextPage } from 'next';
import DefaultLayout from '@components/_templates/DefaultLayout';
import { getPostsById } from '@hooks/fetch/spring/posts/useGetPostsById';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import styled from '@emotion/styled';
import Divider from '@components/_atoms/Divider';
import Typography from '@components/_atoms/Typography';
import { TypoVariant } from '@constants/atoms';
import { gray, primary, purple, white } from '@styles/Colors';
import SummaryTextList from '@components/_templates/career/SummaryTextList';
import BlogReadGoodContentCardList from '@components/_templates/blog/BlogReadGoodContentCard/list';

import { GetPostsByIdData, GetPostsCategoryByIdData } from 'models/swagger-spring/data-contracts';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { getPostsCategoryById } from '@hooks/fetch/spring/posts-category/useGetPostsCategoryById';
import { useGetCachePostsCategoryById } from '@hooks/fetch/spring/posts-category/useGetPostsCategory';

const BlogDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 80px;
  padding-bottom: 112px;
`;

const BlogDetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;
`;

const BlogDetailBannerWrapper = styled.div`
  padding-top: 56px;
`;

const BlogDetailContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1080px;
  margin: 0 auto;
  gap: 120px;
`;

const BlogDetailContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  line-height: 45px;

  img {
    width: 100%;
    max-width: 1080px;
    // padding-top: 10px;
    object-fit: contain;
  }
`;

const BlogDetailContentTitleContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
  align-items: center;
`;

const BlogDetailContentTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BlogDetailBottomMoreWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const BlogDetailBottomMoreContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
`;

const BlogDetailBottomMoreButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BlogReadTogetherGoodContentContainer = styled.div`
  display: flex;
  height: 646px;
  background: ${gray.gray1};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`;

const BlogReadTogetherGoodContentWrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: 1080px;
  height: 486px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 40px;
  flex-shrink: 0;
`;

const EventButton = styled.span`
  display: flex;
  width: 100%;
  height: 100%;
  max-height: 165px;
  padding: 13px 24px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;

  cursor: pointer;
  border-radius: 8px;
  background: ${primary.blue};

  color: ${white};
  text-align: center;
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: -0.32px;
`;

const EventNoticeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const EventNoticeContentWrapper = styled.div`
  background-color: ${gray.gray1};
  padding: 20px 30px;
  display: flex;
  flex-direction: column;
`;

type Props = {
  posts: GetPostsByIdData;
  postsCategory: GetPostsCategoryByIdData;
};

const BlogDetail: NextPage<Props> = ({ posts, postsCategory }) => {
  const router = useRouter();
  const { i18n, t } = useTranslation('blog');

  const ko = i18n.language === 'ko';

  return (
    <>
      <DefaultLayout>
        <BlogDetailContainer>
          <BlogDetailWrapper>
            <BlogDetailBannerWrapper>
              <Image
                src={`${process.env.NEXT_PUBLIC_CDN_URL}${posts?.data?.bannerImageUrl}`}
                alt="blog-detail-banner"
                width={5760}
                height={1320}
                objectFit="cover"
              />
            </BlogDetailBannerWrapper>

            <BlogDetailContentContainer>
              <BlogDetailContentTitleContainer>
                <SummaryTextList
                  summaryList={[postsCategory.data?.name || '', posts.data?.createdAt || '']}
                  fontSize={12}
                  activeIndex={0}
                  activeColor={purple.purple3}
                  defaultColor={gray.gray6}
                />
                <BlogDetailContentTitleWrapper>
                  <Typography variant={TypoVariant.H4}>{posts?.data?.title}</Typography>
                  <Typography variant={TypoVariant.MH2} color={gray.gray6}>
                    {posts?.data?.simpleContent}
                  </Typography>
                </BlogDetailContentTitleWrapper>
              </BlogDetailContentTitleContainer>
              <BlogDetailContentWrapper>
                <Divider />
                <div dangerouslySetInnerHTML={{ __html: posts?.data?.content || '' }} />
                <Divider />

                {/** 이벤트는 KO만 */}
                {ko && posts.data?.isEvent && (
                  <>
                    {posts.data?.eventUrl && (
                      <EventButton onClick={() => router.push(posts.data?.eventUrl || '')}>이벤트 참여하기</EventButton>
                    )}
                    <EventNoticeWrapper>
                      <Typography variant={TypoVariant.H6}>안내사항</Typography>
                      <EventNoticeContentWrapper>
                        <Typography variant={TypoVariant.B4}>
                          - 위 사항은 steelboso 사정에 의해 변경될 수 있으며 변경 시 홈페이지를 통해 별도 공지 합니다.
                          <br />
                          - 업로드 된 자재 리스트와 가격은 steelboso에 귀속됨을 알려드립니다.
                          <br />
                          - 이벤트 종료 후 모든 개인정보는 파기됨을 알려드립니다.
                          <br />
                          - 중복 당첨은 불가하며, 부정한 방법으로 이벤트에 참여한 경우 당첨이 취소 될 수 있습니다.
                          <br />
                          - 상품 지급을 위한 정보 제공 미동의, 전화 및 SMS 수신거부 등의 사유로 상품 지급이 불가한 경우
                          차순위로 넘어갈 수 있습니다.
                          <br />
                          - 미 수령에 대한 책임은 고객 본인에게 있으며, 이로 인한 상품 재 발송은 불가 합니다.
                          <br />
                          - 당첨된 상품에 대해 현금으로의 환급은 불가합니다.
                          <br />
                          - 상품 조기 소진 시 사전 고지 없이 사양이 일부 변경될 수 있습니다.
                          <br />- 기타 이벤트 관련 문의는 steelboso 고객센터(1670-1621)로 문의주시기 바랍니다.
                        </Typography>
                      </EventNoticeContentWrapper>
                    </EventNoticeWrapper>
                  </>
                )}
              </BlogDetailContentWrapper>
            </BlogDetailContentContainer>
          </BlogDetailWrapper>

          {/*           <BlogDetailBottomMoreWrapper>
            <BlogDetailBottomMoreContentWrapper>
              <Typography variant={TypoVariant.SH2}>어떤 자재를 찾으세요?</Typography>
              <Typography variant={TypoVariant.B4} color={gray.gray7}>
                스틸보소가 스틸 자재를 쉽고 빠르게 거래할 수 있도록 모든 과정을 도와 드릴게요.
              </Typography>
            </BlogDetailBottomMoreContentWrapper>
            <BlogDetailBottomMoreButtonWrapper>
              <LinkButton link="">공유하기</LinkButton>
              <PrimaryButton>스틸보소 둘러보기</PrimaryButton>
            </BlogDetailBottomMoreButtonWrapper>
          </BlogDetailBottomMoreWrapper> */}
        </BlogDetailContainer>

        <BlogReadTogetherGoodContentContainer>
          <BlogReadTogetherGoodContentWrapper>
            <Typography variant={TypoVariant.H6}>{t('together-read-content')}</Typography>
            <BlogReadGoodContentCardList
              size={3}
              excludePostsId={posts.data?.id}
              language={postsCategory.data?.language!}
            />
          </BlogReadTogetherGoodContentWrapper>
        </BlogReadTogetherGoodContentContainer>
      </DefaultLayout>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { params, locale } = context;
  const id = params?.id as string;

  const posts = await getPostsById(Number(id));

  let postsCategory;
  if (posts.data?.id) {
    postsCategory = await getPostsCategoryById(posts.data?.postsCategoryId);
  }

  // 서버 측에서 번역 데이터를 가져옵니다.
  const translations = await serverSideTranslations(locale!, ['blog', 'common', 'component']);

  // 서버 측에서 데이터와 번역 데이터를 props로 반환합니다.
  return {
    props: {
      posts,
      postsCategory,
      ...translations,
    },
  };
};

export default BlogDetail;
